import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useState } from "react";
import { Box, Button, Grid2, TextField, Typography } from "@mui/material";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import { ReadOnlyField } from "@/pages/reports/rollshop-operations/components/ReadOnlyField";
import type { ProductChange } from "../models/ProductChange";
import { ProductSizeSelector } from "./ProductSizeSelector";

interface ProductChangeDetailViewerProps {
	change: ProductChange | null;
	isSaving: boolean;
	isRecalculating: boolean;
	onSave: (change: ProductChange) => void;
	onClose: () => void;
	onRecalculate: (id: number) => void;
}

export const ProductChangeDetailViewer = ({
	change,
	isSaving,
	isRecalculating,
	onSave,
	onClose,
	onRecalculate,
}: ProductChangeDetailViewerProps) => {
	const [formData, setFormData] = useState<ProductChange | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const isNewChange = !change?.Id;

	useEffect(() => {
		if (change) {
			setFormData(sanitizeNullStrings({ ...change }));
		} else {
			setFormData(null);
		}
		setErrors({});
	}, [change]);

	const handleChange = (field: keyof ProductChange, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
		setErrors((prev) => ({ ...prev, [field]: "" }));
	};

	const validate = (data: ProductChange | null): Record<string, string> => {
		const newErrors: Record<string, string> = {};
		if (!data?.InitialProductSizeId) {
			newErrors.InitialProductSizeId = "From product is required";
		}
		if (!data?.FinalProductSizeId) {
			newErrors.FinalProductSizeId = "To product is required";
		}
		if (
			data?.InitialProductSizeId &&
			data?.FinalProductSizeId &&
			data.InitialProductSizeId === data.FinalProductSizeId
		) {
			newErrors.FinalProductSizeId = "From and To cannot be the same product";
		}
		return newErrors;
	};

	const handleSelectChange = (field: keyof ProductChange, value: unknown) => {
		handleChange(field, value);

		if (isNewChange) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const validationErrors = validate(updatedData);
		setErrors(validationErrors);

		if (Object.keys(validationErrors).length > 0) return;

		if (change?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleBlurSave = (field: keyof ProductChange, value: unknown) => {
		if (isNewChange) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const validationErrors = validate(updatedData);
		if (Object.keys(validationErrors).length > 0) return;

		if (change?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof ProductChange,
		rawValue: string,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		const validationErrors = validate(formData);
		setErrors(validationErrors);

		if (formData && Object.keys(validationErrors).length === 0) {
			onSave(formData);
		}
	};

	if (!change) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a product change to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={
					isNewChange
						? "New Product Change"
						: `${formData.InitialProductSizeName ?? "ALL"} → ${formData.FinalProductSizeName ?? ""}`
				}
				isSaving={isSaving || isRecalculating}
				onClose={onClose}
				actions={
					isNewChange ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Product" icon={<CompareArrowsOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<ProductSizeSelector
									value={formData.InitialProductSizeId}
									onChange={(value) =>
										handleSelectChange("InitialProductSizeId", value)
									}
									label="From"
									fullWidth
									size="small"
									required
									error={!!errors.InitialProductSizeId}
									helperText={errors.InitialProductSizeId}
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<ProductSizeSelector
									value={formData.FinalProductSizeId}
									onChange={(value) =>
										handleSelectChange("FinalProductSizeId", value)
									}
									label="To"
									fullWidth
									size="small"
									required
									error={!!errors.FinalProductSizeId}
									helperText={errors.FinalProductSizeId}
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Goal" icon={<TimerOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Goal Time (min)"
									type="number"
									value={formData.GoalTime ?? ""}
									onChange={(e) =>
										handleChange(
											"GoalTime",
											e.target.value === ""
												? null
												: Number.parseFloat(e.target.value),
										)
									}
									onBlur={(e) =>
										handleNumericBlurSave("GoalTime", e.target.value)
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { step: 0.01 },
									}}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					{!isNewChange && (
						<DetailSection
							title="History"
							icon={<HistoryOutlinedIcon />}
							actions={
								<Button
									variant="contained"
									color="primary"
									size="small"
									startIcon={<RefreshIcon />}
									onClick={() => formData.Id && onRecalculate(formData.Id)}
									disabled={isRecalculating || !formData.Id}
								>
									{isRecalculating ? "RECALCULATING..." : "RECALCULATE"}
								</Button>
							}
						>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField
										label="History Quantity"
										value={
											formData.HistoryQuantity != null
												? String(formData.HistoryQuantity)
												: ""
										}
										numeric
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField
										label="History Time (min)"
										value={
											formData.HistoryTime != null
												? formData.HistoryTime.toFixed(2)
												: ""
										}
										numeric
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
					)}
				</Box>
			</DetailBody>
		</Box>
	);
};
